import React, { useState, useEffect } from "react";
import axios from "axios";
import { useSnackbar } from "notistack";
import { useHistory, useLocation, Redirect } from 'react-router-dom';
import { AbsenceTypology } from "helpers/Enums";
import moment from "moment";

interface IAbsencesContext {
    recordCount: number
    search: ISearchRequest
    absences: IAbsence[]
    isLoading: boolean

    doSearch: (search: ISearchRequest) => void
    handleChangePage: (page: number) => void
    searchAbsences: (searchRequest: ISearchRequest) => Promise<void>
    deleteAbsence: (absenceId: string) => Promise<void>
    handleChange: (e: any) => void
}

export interface IAbsence {
    absenceId: string
    userName: string
    userLabel: string
    typology: AbsenceTypology
    otherTypology: string
    notes: string
    startDate: string
    endDate: string
}

interface ISearchRequest {
    userName: string
    dateFrom?: moment.Moment | null
    dateTo?: moment.Moment | null
    pageNr: number
    pageSize: number
}

export let absencesContext = React.createContext({} as IAbsencesContext);

let { Provider } = absencesContext;

let AbsencesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    let { enqueueSnackbar } = useSnackbar();
    let history = useHistory();
    let location = useLocation();

    let [isLoading, setLoading] = useState(true);

    let [absences, setAbsences] = useState<IAbsence[]>([]);
    let [recordCount, setTotalRecords] = useState(0);

    let [search, setSearch] = useState<ISearchRequest>({
        userName: '',
        dateFrom: null,
        dateTo: null,
        pageNr: 0,
        pageSize: 15
    });

    useEffect(() => {
        if (location.state)
            processState(location.state);
    }, [location.state])

    if (!location.state)
        return <Redirect to={{ pathname: location.pathname, state: { search } }} />


    let processState = (state: any) => {
        if (state.search) {
            let request = {
                ...state.search,
                dateFrom: state.search?.dateFrom != null ? moment(state.search.dateFrom).utc(true) : null,
                dateTo: state.search?.dateTo != null ? moment(state.search.dateTo).utc(true) : null
            }
            setSearch(request);
            searchAbsences(request);
        }
    }

    let doSearch = (values: ISearchRequest) => {
        let request = {
            ...values,
            dateFrom: values.dateFrom != null ? moment(values.dateFrom).toDate() : null,
            dateTo: values.dateTo != null ? moment(values.dateTo).toDate() : null,
            pageNr: values.pageNr > 0 ? 0 : values.pageNr
        }

        history.push(location.pathname, {
            search: request,
            selectedAbsences: [],
            allPageSelected: false
        });
    }

    let searchAbsences = async (searchRequest: ISearchRequest) => {
        try {
            setLoading(true);
            let response = await axios.post('/api/Absence/SearchAbsences', searchRequest);
            setAbsences(response.data.items);
            setTotalRecords(response.data.recordCount);
        } catch (error) { }
        finally {
            setLoading(false);
        }
    }

    let handleChangePage = (page: number) => {
        let request = {
            ...search,
            dateFrom: search.dateFrom != null ? moment(search.dateFrom).toDate() : null,
            dateTo: search.dateFrom != null ? moment(search.dateTo).toDate() : null,
            pageNr: page
        }

        window.scrollTo(0, 0);
        history.push(location.pathname, {
            search: request,
            selectedAbsences: [],
            allPageSelected: false
        });
    }

    let deleteAbsence = async (absenceId: string) => {
        if (window.confirm("Vuoi eliminare questa Assenza?")) {
            try {
                await axios.delete(`/api/Absence/DeleteAbsence/${absenceId}`)
                setAbsences(absences.filter(absence => absence.absenceId !== absenceId));
                setTotalRecords(recordCount - 1);
                enqueueSnackbar("Absence deleted", { variant: 'success' });
            } catch (error) { }
        }
    }

    let handleChange = (e: any) => {
        let request = {
            ...e
        };
        setSearch(request);
    }

    return (
        <Provider value={{
            recordCount,
            search,
            absences,
            isLoading,

            doSearch,
            handleChangePage,
            searchAbsences,
            deleteAbsence,
            handleChange
        }}>
            {children}
        </Provider>
    );
};

export default AbsencesProvider;